/**
 * CONSAI Business App - Main App Router
 */
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';

// Context Providers
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Layout Components
import Header from './components/Header';

// Global Components
import PremiumTrialNudgePopup from './components/PremiumTrialNudgePopup';
import OnboardingTour from './components/OnboardingTour';

// Pages
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminAnalytics from './pages/AdminAnalytics';
import AuthCallback from './components/AuthCallback';

// Phase 2 Pages
import Payment from './pages/Payment';
import Questionnaire from './pages/Questionnaire';
import ReportView from './pages/ReportView';
import AdminVouchers from './pages/AdminVouchers';
import AdminReports from './pages/AdminReports';

// Phase 3 Pages
import Customers from './pages/Customers';
import Inventory from './pages/Inventory';
import Sales from './pages/Sales';
import Invoices from './pages/Invoices';
import Credit from './pages/Credit';
import Analytics from './pages/Analytics';
import PlanComparison from './pages/PlanComparison';
import AIAssistant from './pages/AIAssistant';
import Suppliers from './pages/Suppliers';

// Thank You Pages
import ThankYouStarter from './pages/ThankYouStarter';
import ThankYouPremium from './pages/ThankYouPremium';
import ThankYouReport from './pages/ThankYouReport';

// Protected Route Component for Client with Global Header
const ProtectedRoute = ({ children, showHeader = true }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // If user data passed from AuthCallback, render immediately
  if (location.state?.user) {
    return children;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-white/80 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Layout wrapper for pages that need the global header
const PageWithHeader = ({ children, title }) => {
  return (
    <>
      <Header title={title} />
      {children}
    </>
  );
};

// Protected Route Component for Admin
const AdminProtectedRoute = ({ children }) => {
  const { isAdminAuthenticated, loading } = useAuth();
  const location = useLocation();

  // If admin data passed from AuthCallback, render immediately
  if (location.state?.admin) {
    return children;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-white/80 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!isAdminAuthenticated) {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

// Main App Router - Check for session_id in hash BEFORE rendering routes
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  // This prevents race conditions with ProtectedRoute
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Landing Page */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<AdminLogin />} />
      <Route 
        path="/admin/dashboard" 
        element={
          <AdminProtectedRoute>
            <AdminDashboard />
          </AdminProtectedRoute>
        } 
      />
      <Route 
        path="/admin/analytics" 
        element={
          <AdminProtectedRoute>
            <AdminAnalytics />
          </AdminProtectedRoute>
        } 
      />
      <Route 
        path="/admin/vouchers" 
        element={
          <AdminProtectedRoute>
            <AdminVouchers />
          </AdminProtectedRoute>
        } 
      />
      <Route 
        path="/admin/reports" 
        element={
          <AdminProtectedRoute>
            <AdminReports />
          </AdminProtectedRoute>
        } 
      />

      {/* Protected Client Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/payment" 
        element={
          <ProtectedRoute>
            <Payment />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/questionnaire" 
        element={
          <ProtectedRoute>
            <Questionnaire />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/report" 
        element={
          <ProtectedRoute>
            <ReportView />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/customers" 
        element={
          <ProtectedRoute>
            <Customers />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/stock" 
        element={
          <ProtectedRoute>
            <Inventory />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/suppliers" 
        element={
          <ProtectedRoute>
            <Suppliers />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/sales" 
        element={
          <ProtectedRoute>
            <Sales />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/invoices" 
        element={
          <ProtectedRoute>
            <Invoices />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/credit" 
        element={
          <ProtectedRoute>
            <Credit />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/analytics" 
        element={
          <ProtectedRoute>
            <Analytics />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/plans" 
        element={
          <ProtectedRoute>
            <PlanComparison />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/ai-assistant" 
        element={
          <ProtectedRoute>
            <AIAssistant />
          </ProtectedRoute>
        } 
      />

      {/* Thank You Pages */}
      <Route 
        path="/thank-you/starter" 
        element={
          <ProtectedRoute>
            <ThankYouStarter />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/thank-you/premium" 
        element={
          <ProtectedRoute>
            <ThankYouPremium />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/thank-you/report" 
        element={
          <ProtectedRoute>
            <ThankYouReport />
          </ProtectedRoute>
        } 
      />

      {/* Catch all - redirect to landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <AppRouter />
          <OnboardingTour />
          <PremiumTrialNudgePopup />
          <Toaster 
            position="top-right" 
            theme="dark"
            richColors 
            closeButton
            toastOptions={{
              style: {
                fontFamily: 'Manrope, sans-serif',
                background: '#1A1A1C',
                border: '1px solid rgba(255,255,255,0.08)',
                color: '#F4F4F5'
              }
            }}
          />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
